import React, { ReactNode, useMemo, useState } from 'react';
import { Controller, FieldValues, useController } from 'react-hook-form';
import type { GroupBase } from 'react-select';
import { components, createFilter } from 'react-select';
import type Select from 'react-select/base';
import CreatableSelect from 'react-select/creatable';

import CloseXs from '@/assets/icons/CloseXs';
import { Label } from '@/components/UI/Label/Label';
import { ActiviteitVoorBewegingModel } from '@/queries/queryModels/activiteitenVoorBewegingModel';
import { reactSelectOnBlurWorkaround } from '@/utils/helpers';

import { FieldError } from '../FieldError';
import { FieldProps } from '../types';

import { prioritizeGroupedOptions } from './selectSearchUtils';

import styles from '../Fields.module.css';

export type GroupedOption = {
  label: string;
  options: { value: string; label: string; chipLabel?: string }[];
};

type SelectFieldProps<T extends FieldValues> = FieldProps<T> & {
  testId?: string;
  createLabel?: string;
  enableSelectOneForMultiple?: boolean;
  matchFromStart?: boolean;
  prioritizeGroupsBySearchInput?: boolean;
  onChangeCallback?: (
    p:
      | string
      | number
      | ActiviteitVoorBewegingModel
      | {
          treinnummer: number;
          dienstregelVersie: number;
        }
      | null,
  ) => void;
  options?: {
    label: string | ReactNode;
    value?: string | number | ActiviteitVoorBewegingModel | { treinnummer: number; dienstregelVersie: number };
    chipLabel?: string;
  }[];
} & Omit<React.ComponentPropsWithoutRef<typeof CreatableSelect>, keyof FieldProps<T> | 'control' | 'name'>;

export const CreatableSelectField = <T extends FieldValues = FieldValues>({
  control,
  onChangeCallback,
  enableSelectOneForMultiple = false,
  options,
  name,
  label,
  testId,
  required,
  rules,
  placeholder,
  defaultValue,
  createLabel,
  isMulti,
  isClearable,
  openMenuOnClick = true,
  isSearchable,
  matchFromStart,
  prioritizeGroupsBySearchInput = false,
  ref,
  ...rest
}: SelectFieldProps<T> & { ref?: React.Ref<Select<unknown, boolean, GroupBase<unknown>>> }) => {
  const { fieldState } = useController({
    name,
    control,
    rules: {
      required: required ? 'Dit veld is verplicht.' : undefined,
      ...rules,
    },
    defaultValue,
  });

  const [inputValueChange, setInputValueChange] = useState('');

  const filterConfig = {
    matchFrom: matchFromStart ? ('start' as const) : ('any' as const),
  };
  const normalizedInput = inputValueChange.trim().toLocaleLowerCase();
  const prioritizedOptions = useMemo(() => {
    if (!prioritizeGroupsBySearchInput || !normalizedInput || !options) return options;

    return prioritizeGroupedOptions(options, normalizedInput);
  }, [normalizedInput, options, prioritizeGroupsBySearchInput]);

  const getValue = (field) => {
    if (!field.value) return isMulti ? [] : null;
    if (isMulti && Array.isArray(field.value)) {
      return field.value.map((v) => {
        // If v is already an option object (e.g. from Controller defaultValue), return it directly
        if (v && typeof v === 'object' && 'label' in v && 'value' in v) return v;
        const allOptions = options?.flatMap((o: any) => (o.options ? o.options : [o])) ?? [];
        return allOptions.find((o) => JSON.stringify(o.value) === JSON.stringify(v)) ?? { label: v, value: v };
      });
    }
    return options?.find((c) => JSON.stringify(c.value) === JSON.stringify(field.value));
  };

  const handleMultiChange = (val: any, field) => {
    const values = val ? val.map((v: any) => v.value) : null;
    field.onChange(values);
    onChangeCallback?.(values);
  };

  const handleSingleChange = (val: any, field) => {
    const value = val ? val.value : null;
    field.onChange(value);
    onChangeCallback?.(value);
  };

  const MultiValue = (props) => {
    return props.data.chipLabel ? (
      <components.MultiValue {...props}>{props.data.chipLabel}</components.MultiValue>
    ) : (
      <components.MultiValue {...props}>{props.data.label}</components.MultiValue>
    );
  };

  const CustomMultiValueRemove = (props) => {
    return (
      <div style={{ display: 'flex' }} {...props.innerProps}>
        <CloseXs fill='currentColor' />
      </div>
    );
  };

  return (
    <div>
      <Label htmlFor={`create-select-${testId}`} aria-label={label}>
        {label}
      </Label>
      <div className={styles.selectContainer}>
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          render={({ field }) => (
            <CreatableSelect
              {...rest}
              id={`create-select-${testId}`}
              formatCreateLabel={(inputValue) => `${createLabel} ${inputValue}`} // NOSONAR
              placeholder={placeholder}
              className='react-select-container'
              classNamePrefix='react-select'
              defaultValue={defaultValue}
              noOptionsMessage={() => 'Geen opties'}
              options={prioritizedOptions}
              value={enableSelectOneForMultiple && defaultValue?.length > 0 ? defaultValue : getValue(field)}
              onBlur={reactSelectOnBlurWorkaround}
              inputId={testId}
              // ref={field.ref}
              ref={ref}
              filterOption={createFilter(filterConfig)}
              // multi select
              onChange={isMulti ? (val) => handleMultiChange(val, field) : (val) => handleSingleChange(val, field)}
              isMulti={isMulti}
              isClearable={isClearable}
              isSearchable={isSearchable}
              styles={{
                ...rest.styles,
                control: (base) => ({
                  ...base,
                  borderRadius: 'var(--tractie-border-radius-m)',
                }),
              }}
              components={
                isMulti && inputValueChange.length < 1
                  ? {
                      DropdownIndicator: null,
                      MultiValueRemove: CustomMultiValueRemove,
                      MultiValue,
                    }
                  : { MultiValueRemove: CustomMultiValueRemove, MultiValue }
              }
              onInputChange={(input) => setInputValueChange(input)}
              menuIsOpen={openMenuOnClick ? undefined : inputValueChange.length > 0}
            />
          )}
        />
        <FieldError width='auto' error={fieldState.error} />
      </div>
    </div>
  );
};
